import {
    EMPTY_EMPLOYMENT_ATTESTATION,
    employmentAttestationFrom,
    isEmploymentAttestationComplete,
} from '@/shared/utils/studentEmployment';
import {
    EMPTY_REQUIREMENT_ANSWERS,
    requirementAnswersFrom,
    requirementAttestationsBody,
} from '@/shared/utils/requirementAnswers';
import { getProgramDefinition } from './programs';
import { getErrors as aboutYouErrors, MAJOR_ID } from './blocks/AboutYou';
import { getErrors as accuracyErrors } from './blocks/AccuracyConfirmation';
import { getErrors as campusEmploymentErrors } from './blocks/CampusEmployment';
import { getErrors as generalPoolErrors } from './blocks/GeneralPool';
import {
    getErrors as requirementAnswersErrors,
    courseId,
} from './blocks/RequirementAnswers';
import {
    getErrors as positionCommitmentsErrors,
    commitmentId,
} from './blocks/PositionCommitments';
import { getErrors as resumeErrors } from './blocks/Resume';

export const PROGRAM_CHOICE_ID = 'sap-program-choice';
export const POSITIONS_ID = 'sap-positions';
// The most positions one application may list: the server's MAX_POSITIONS
// (server/src/services/positionChoices.js).
export const MAX_POSITIONS = 10;
export const INTEREST_STATEMENT_ID = 'sap-interest-statement';
export const ELIGIBILITY_ID = 'sap-eligibility';
export const PRIOR_EXPERIENCE_ID = 'sap-prior-experience';

/**
 * A form with nothing answered and no program chosen. With positions on
 * (hiring), the student ranks open positions: `positions` is the ordered list
 * of { applicationId, summary? }. Without it the legacy course list is used
 * and `positions` is null.
 */
export function emptyForm(positionsOn = false) {
    return {
        program: null,
        academicBackground: {
            major: '',
            secondMajor: '',
            minor: '',
        },
        availability: { hoursPerWeek: '', schedule: [] },
        coursePreferences: [],
        positions: positionsOn ? [] : null,
        priorExperience: '',
        interestStatement: '',
        employmentAttestation: EMPTY_EMPLOYMENT_ATTESTATION,
        references: [],
        generalPool: null,
        campusEmployment: {},
        // The student's answers per chosen position:
        // { courses: { [positionId]: boolean }, commitments: { [positionId]: boolean } }
        requirementAnswers: EMPTY_REQUIREMENT_ANSWERS,
        confirmsSubmissionAccuracy: false,
        // Typed in About You when the profile has no student ID. It belongs
        // to the profile: buildStudentApplicationBody never reads it.
        profileStudentId: '',
        programAnswers: {},
    };
}

/**
 * The form a stored application fills in, for editing or resuming.
 * `positions` (ids, in rank order) fills the ranked positions when hiring is
 * on; the caller adds any live candidacy the stored list lacks.
 */
export function formFromApplication(app, positions = null) {
    const definition = getProgramDefinition(app.program);
    const stored = app.campusEmployment || {};
    const campusEmployment = {};
    if (stored.workStudy) campusEmployment.workStudy = stored.workStudy;
    if (typeof stored.holdsCampusPosition === 'boolean') {
        campusEmployment.holdsCampusPosition = stored.holdsCampusPosition;
    }
    if (stored.hoursPerWeek != null) {
        campusEmployment.hoursPerWeek = stored.hoursPerWeek;
    }
    return {
        ...emptyForm(),
        program: app.program,
        academicBackground: {
            major: app.academicBackground?.major || '',
            secondMajor: app.academicBackground?.secondMajor || '',
            minor: app.academicBackground?.minor || '',
        },
        // course comes back populated; drop any whose course is gone
        coursePreferences: [...(app.coursePreferences || [])]
            .filter((cp) => cp.course && cp.course._id)
            .sort((a, b) => (a.rank || 0) - (b.rank || 0))
            .map((cp, i) => ({ course: cp.course, rank: i + 1 })),
        positions: positions ? positions.map((p) => ({ ...p })) : null,
        priorExperience: app.priorExperience || '',
        interestStatement: app.interestStatement || '',
        availability: {
            hoursPerWeek:
                app.availability?.hoursPerWeek != null
                    ? String(app.availability.hoursPerWeek)
                    : '',
            schedule: (app.availability?.schedule || []).map((s) => ({
                day: s.day,
                startTime: s.startTime,
                endTime: s.endTime,
            })),
        },
        employmentAttestation: employmentAttestationFrom(
            app.employmentAttestation
        ),
        // formSubmitted stays on the server, matched by email
        references: (app.references || []).map((r) => ({
            name: r.name || '',
            email: r.email || '',
            relationship: r.relationship || '',
        })),
        generalPool:
            typeof app.generalPool === 'boolean' ? app.generalPool : null,
        campusEmployment,
        requirementAnswers: requirementAnswersFrom(app.requirementAttestations),
        confirmsSubmissionAccuracy: app.confirmsSubmissionAccuracy === true,
        programAnswers: definition ? definition.fromApplication(app) : {},
    };
}

function campusEmploymentBody(campusEmployment) {
    if (!campusEmployment?.workStudy) return undefined;
    const body = { workStudy: campusEmployment.workStudy };
    if (typeof campusEmployment.holdsCampusPosition === 'boolean') {
        body.holdsCampusPosition = campusEmployment.holdsCampusPosition;
    }
    const hours = campusEmployment.hoursPerWeek;
    if (hours !== '' && hours != null && Number.isFinite(Number(hours))) {
        body.hoursPerWeek = Number(hours);
    }
    return body;
}

/**
 * The request body for a student application, used by every save and every
 * submit so a field added to the questionnaire is added in one place.
 *
 * `form` holds the wizard's state as it stands on screen, with `term` set and
 * `programRules` (the requirements endpoint's answer for the program) once it
 * has loaded. The requirement answers are sent only with the rules: they are
 * the questions the program asks, and without them a save leaves the stored
 * answers as they are.
 * Rows still being typed (a reference without a name and email) are left out, and references are always
 * sent so removing the last one is saved too. The chosen program's
 * `toPayload` supplies its own fields. `profileStudentId` and the program's
 * resume are never sent: the student ID is saved to the profile, and the
 * resume lives on the profile too.
 */
export function buildStudentApplicationBody(form) {
    const { academicBackground, availability } = form;
    const body = {
        program: form.program,
        term: form.term,
        academicBackground: {
            major: academicBackground.major,
            secondMajor: academicBackground.secondMajor,
            minor: academicBackground.minor,
        },
        availability: {
            hoursPerWeek: availability.hoursPerWeek
                ? parseInt(availability.hoursPerWeek, 10)
                : undefined,
            schedule: availability.schedule,
        },
        priorExperience: form.priorExperience,
        interestStatement: form.interestStatement,
        employmentAttestation: form.employmentAttestation,
        references: form.references.filter(
            (r) => r.name.trim() && r.email.trim()
        ),
        confirmsSubmissionAccuracy: form.confirmsSubmissionAccuracy === true,
    };

    if (Array.isArray(form.positions)) {
        body.positions = form.positions.map((p) => p.applicationId);
    } else {
        body.coursePreferences = form.coursePreferences.map((cp) => ({
            course: cp.course._id,
            rank: cp.rank,
        }));
    }
    if (typeof form.generalPool === 'boolean') {
        body.generalPool = form.generalPool;
    }
    const campusEmployment = campusEmploymentBody(form.campusEmployment);
    if (campusEmployment) body.campusEmployment = campusEmployment;
    if (form.programRules?.assertions) {
        body.requirementAttestations = requirementAttestationsBody(
            form.requirementAnswers,
            form.programRules.assertions,
            Array.isArray(form.positions)
                ? form.positions.map((p) => p.applicationId)
                : []
        );
    }

    const definition = getProgramDefinition(form.program);
    if (definition)
        Object.assign(body, definition.toPayload(form.programAnswers, form));

    return body;
}

/**
 * Whether the student has entered anything beyond choosing a program. A
 * program choice alone is not worth a draft the student can't delete, so
 * leaving the page saves only once this is true (or a draft already exists).
 * The student ID typed in About You is not counted: it is saved to the
 * profile, never to the draft.
 */
export function hasEnteredAnswers(form) {
    const text = (value) => String(value ?? '').trim().length > 0;
    const { academicBackground, availability, requirementAnswers } = form;
    const emptyAnswers = getProgramDefinition(form.program)?.emptyAnswers || {};
    const answered = (value, empty) => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'string') return text(value);
        return value !== empty && value != null;
    };
    return (
        text(academicBackground.major) ||
        text(academicBackground.secondMajor) ||
        text(academicBackground.minor) ||
        form.coursePreferences.length > 0 ||
        (form.positions?.length ?? 0) > 0 ||
        text(form.priorExperience) ||
        text(form.interestStatement) ||
        text(availability.hoursPerWeek) ||
        availability.schedule.length > 0 ||
        Object.values(form.employmentAttestation).some((a) => a != null) ||
        form.references.length > 0 ||
        typeof form.generalPool === 'boolean' ||
        Object.keys(form.campusEmployment || {}).length > 0 ||
        Object.keys(requirementAnswers.courses || {}).length > 0 ||
        Object.values(requirementAnswers.commitments || {}).some(Boolean) ||
        form.confirmsSubmissionAccuracy === true ||
        Object.entries(form.programAnswers || {}).some(([key, value]) =>
            answered(value, emptyAnswers[key])
        )
    );
}

const needs = (id, label) => [{ id, label }];

/**
 * What is still open in one block, as [{ id, label }] where `id` is the DOM
 * id of the first control to fix. Empty when the block is complete.
 * `userProfile` decides whether About You asks for the student ID.
 */
export function getBlockErrors(block, form, userProfile) {
    switch (block) {
        case 'program':
            return form.program ? [] : needs(PROGRAM_CHOICE_ID, 'Program');
        case 'aboutYou':
            return aboutYouErrors(form, userProfile);
        case 'positions':
            // With positions on, a Yes to the pool stands in for a choice
            if (Array.isArray(form.positions)) {
                return form.positions.length > 0 || form.generalPool === true
                    ? []
                    : needs(POSITIONS_ID, 'At least one position');
            }
            return form.coursePreferences.length > 0
                ? []
                : needs(POSITIONS_ID, 'At least one course');
        case 'generalPool':
            return generalPoolErrors(form);
        case 'interestStatement':
            return form.interestStatement.trim()
                ? []
                : needs(INTEREST_STATEMENT_ID, 'Interest statement');
        case 'programQuestions': {
            const definition = getProgramDefinition(form.program);
            if (!definition) return [];
            return (definition.getQuestionErrors || definition.getErrors)(
                form.programAnswers
            );
        }
        case 'programEligibility': {
            const definition = getProgramDefinition(form.program);
            return definition?.getEligibilityErrors
                ? definition.getEligibilityErrors(form.programAnswers)
                : [];
        }
        case 'eligibility':
            return isEmploymentAttestationComplete(form.employmentAttestation)
                ? []
                : needs(ELIGIBILITY_ID, 'Employment eligibility questions');
        case 'campusEmployment':
            return campusEmploymentErrors(form);
        case 'requirementAnswers':
            return requirementAnswersErrors(form, form.programRules);
        case 'positionCommitments':
            return positionCommitmentsErrors(form);
        case 'resume':
            return resumeErrors(form, form.programRules, userProfile);
        case 'review':
            return accuracyErrors(form);
        default:
            return [];
    }
}

/** The open items on one step of `steps`, in the order its blocks appear. */
export function getStepErrors(steps, stepIndex, form, userProfile) {
    const blocks = steps[stepIndex]?.blocks || [];
    return blocks.flatMap((block) => getBlockErrors(block, form, userProfile));
}

/**
 * Where a resumed draft opens: its first step still needing an answer, or the
 * last step (Review & Submit) when every earlier step is done. 0-based.
 */
export function firstIncompleteStep(steps, form, userProfile) {
    for (let index = 0; index < steps.length - 1; index += 1) {
        if (getStepErrors(steps, index, form, userProfile).length > 0) {
            return index;
        }
    }
    return steps.length - 1;
}

// Where each field a submit refusal names is answered: the block that owns
// it and the control to focus. The server names the field, the wizard
// finds the step.
const FIELD_TARGETS = {
    studentId: { block: 'aboutYou', id: 'sa-student-id' },
    major: { block: 'aboutYou', id: MAJOR_ID },
    employmentAttestation: { block: 'eligibility', id: ELIGIBILITY_ID },
    generalPool: { block: 'generalPool', id: 'sa-general-pool' },
    'campusEmployment.workStudy': {
        block: 'campusEmployment',
        id: 'sa-work-study',
    },
    'campusEmployment.holdsCampusPosition': {
        block: 'campusEmployment',
        id: 'sa-holds-campus-position',
    },
    'laDetails.isReturning': {
        block: 'programEligibility',
        id: 'la-is-returning-yes',
    },
    'laDetails.canAttendOrientation': {
        block: 'programEligibility',
        id: 'la-can-attend-orientation-yes',
    },
    'laDetails.canAttendPedagogy': {
        block: 'programEligibility',
        id: 'la-can-attend-pedagogy-yes',
    },
    'laDetails.priorRoles': {
        block: 'programQuestions',
        id: 'la-prior-role-ta',
    },
    'laDetails.otherRoleInterest': {
        block: 'programQuestions',
        id: 'la-other-role-tutor',
    },
    interestStatement: {
        block: 'programQuestions',
        id: 'la-interest-statement',
    },
    priorExperience: { block: 'programQuestions', id: 'la-prior-experience' },
    positions: { block: 'positions', id: POSITIONS_ID },
    resume: { block: 'resume', id: 'sap-resume-file' },
    'commitments.acknowledged': {
        block: 'programQuestions',
        id: 'la-commitments-acknowledged',
    },
    confirmsSubmissionAccuracy: {
        block: 'review',
        id: 'sa-confirms-submission-accuracy',
    },
};

// SI asks these two on its own legacy blocks, not in program questions.
const SI_TARGETS = {
    interestStatement: {
        block: 'interestStatement',
        id: INTEREST_STATEMENT_ID,
    },
    priorExperience: { block: 'priorExperience', id: PRIOR_EXPERIENCE_ID },
};

// The server names an unanswered question
// `requirementAttestations.positionCommitment.<positionId>` or
// `requirementAttestations.priorCompletion.<positionId>`.
const COURSE_FIELD = 'requirementAttestations.priorCompletion.';
const COMMITMENT_FIELD = 'requirementAttestations.positionCommitment.';
function requirementTarget(field) {
    if (typeof field !== 'string') return null;
    if (field.startsWith(COURSE_FIELD)) {
        return {
            block: 'requirementAnswers',
            id: `${courseId(field.slice(COURSE_FIELD.length))}-yes`,
        };
    }
    if (field.startsWith(COMMITMENT_FIELD)) {
        return {
            block: 'positionCommitments',
            id: commitmentId(field.slice(COMMITMENT_FIELD.length)),
        };
    }
    return null;
}

/**
 * The step and control a submit refusal's `field` points at: { step, id }
 * with `step` 0-based, or null for a field the wizard does not ask.
 */
export function targetForRefusal(steps, program, field) {
    const target =
        (program === 'si' && SI_TARGETS[field]) ||
        FIELD_TARGETS[field] ||
        requirementTarget(field);
    if (!target) return null;
    const step = steps.findIndex((s) => s.blocks.includes(target.block));
    return step === -1 ? null : { step, id: target.id };
}
